import { useQuery } from '@tanstack/react-query';

import { validateAutomationImport } from '@/generated/hooks/grimoire/v1/import_export';
import type {
  V1MissingConnection,
  V1PhaseSubType,
} from '@/generated/hooks/grimoire/models';

export interface IUseImportValidationParams {
  organizationId?: string;
  workspaceId?: string;
  automationId?: string;
  /** Gate the call so it only fires for drafts. */
  enabled?: boolean;
}

export interface IUseImportValidationResult {
  isImported: boolean;
  missingConnections: readonly V1MissingConnection[];
  missingTriggerKinds: readonly V1PhaseSubType[];
  isLoading: boolean;
}

/**
 * Validates whether a draft was created by importing/moving an automation and,
 * if so, what is still missing before it can run (connections with no match in
 * the workspace and trigger kinds that are not yet configured).
 *
 * The generated `validateImport` endpoint is a POST but is read-only, so it is
 * wrapped in a query here: it fetches once per draft on mount and is cached,
 * rather than firing a mutation from an effect.
 */
/**
 * Query key for the import-validation query. Exported so callers that persist
 * connections/triggers can invalidate it to refresh what the checklist still
 * lists as missing.
 */
export const getValidateAutomationImportQueryKey = (
  organizationId?: string,
  workspaceId?: string,
  automationId?: string,
) =>
  [
    'validateAutomationImport',
    organizationId,
    workspaceId,
    automationId,
  ] as const;

export function useImportValidation({
  organizationId,
  workspaceId,
  automationId,
  enabled = true,
}: IUseImportValidationParams): IUseImportValidationResult {
  const { data, isLoading } = useQuery({
    queryKey: getValidateAutomationImportQueryKey(
      organizationId,
      workspaceId,
      automationId,
    ),
    queryFn: () =>
      validateAutomationImport(
        organizationId as string,
        workspaceId as string,
        automationId as string,
        {},
      ),
    enabled: enabled && !!organizationId && !!workspaceId && !!automationId,
    // Per-draft and stable for the lifetime of the editor; refetch is not
    // needed during normal use but keep it finite rather than Infinity.
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const response = data?.data;

  return {
    isImported: Boolean(response?.is_imported_automation),
    missingConnections: response?.missing_connections ?? [],
    missingTriggerKinds: response?.missing_trigger_kinds ?? [],
    isLoading,
  };
}
